import { AfterViewInit, Component, ElementRef, HostListener, OnInit, input, output, viewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { ButtonModule } from '@progress/kendo-angular-buttons';
import { DialogContentBase, DialogModule, DialogRef, WindowModule } from '@progress/kendo-angular-dialog';
import { FormFieldModule, TextBoxModule } from '@progress/kendo-angular-inputs';
import { LabelModule } from '@progress/kendo-angular-label';

export type ValueValidatorFn = (value: string) => boolean;

@Component({
  selector: 'app-confirmation-dialog-input',
  templateUrl: './confirmation-dialog-input.component.html',
  styleUrls: ['./confirmation-dialog-input.component.scss'],
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule, FormFieldModule, LabelModule,
    TextBoxModule, DialogModule, ButtonModule, WindowModule]
})
export class ConfirmationDialogInputComponent extends DialogContentBase implements OnInit, AfterViewInit {

  /*-------------------------- ViewChild --------------------------------------*/

  public contentInput = viewChild.required<ElementRef<HTMLInputElement>>('contentInput');
 
  /*-------------------------- Input / Outputs --------------------------------*/

  public title = input.required<string>();
  public content = input.required<string>();
  public defaultValue = input<string>('');
  public isValueAcceptable = input<ValueValidatorFn | undefined>(undefined);
  public valueEntered = output<object>();
   public maxLength = input<number | undefined>(undefined);

  /*-------------------------- Public properties ------------------------------*/

  public formGroup!: FormGroup;

  /*-------------------------- Constructor ------------------------------------*/

  constructor(public dialog: DialogRef, private formBuilder: FormBuilder) {
    super(dialog);
  }

  /*-------------------------------- ng ---------------------------------------*/

  public ngOnInit(): void {
    const validators = [this.noWhitespaceValidator(), this.noSlashValidator()];

    if(this.maxLength()) {
      validators.push(Validators.maxLength(this.maxLength()!));
    }
    const callback = this.isValueAcceptable();
    if (callback) {
      validators.push(this.acceptableValueValidator(callback));
    }

    this.formGroup = this.formBuilder.group({
      content: [this.defaultValue(), validators]
    });
  }

  public ngAfterViewInit(): void {
    setTimeout(() => {
      const el = this.contentInput().nativeElement;
      el.focus();
      el.select();
    }, 0);
  }

  /*-------------------------- Validators -------------------------------------*/

  private noWhitespaceValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = (control.value ?? '').toString().trim();
      return value.length === 0 ? { required: true } : null;
    };
  }

  private acceptableValueValidator(callback: ValueValidatorFn): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = (control.value ?? '').toString().trim();
      if (value === '') {
        return null; // let noWhitespaceValidator handle empty
      }
      return callback(value) ? null : { notAcceptable: true };
    };
  } 

  private noSlashValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;

      if (value && /[\/\\]/.test(value)) {
        return { slashNotAllowed: true };
      }

      return null;
    };
  }

  /*-------------------------- Keyboard handling ------------------------------*/

  @HostListener('keydown', ['$event'])
  public onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.onOk();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.onCancel(new PointerEvent('pointerup'));
    }
  }

  /*-------------------------- Event handlers ---------------------------------*/

  public onOk(): void {
    if (this.formGroup.valid) {
      const rawValue: string = (this.formGroup.value.content ?? '').toString().trim();
      this.valueEntered.emit({ success: true, value: rawValue });
      this.dialog.close(true);
    } else {
      this.formGroup.get('content')?.markAsTouched();
      setTimeout(() => this.contentInput().nativeElement.focus(), 0);
    }
  }

  public onCancel(e: PointerEvent): void {
    e.preventDefault();
    this.valueEntered.emit({ success: false, value: '' });
    this.dialog.close(true);
  }
}
